import React, { type CSSProperties, type ReactNode } from 'react'
import styled from '@emotion/styled'
import { useRenderTimeoutControl } from '@mobi/utils/hooks/useRenderTimeoutControl'
import { colors, font, measurements, radius, spacing } from '@mobi/component-library/Foundations'
import { PillButton } from '@mobi/component-library/Base/Buttons/PillButton'
import { Icon } from '@mobi/component-library/Assets/Icon'
import type { IconName } from '@mobi/component-library/Assets/Icon/Icon'

const enum LocalConstants {
  AnimationTimingMs = 300,
}

const headerIconConfig: Record<'warning' | 'confirm', HeaderIconConfig> = {
  warning: {
    name: 'LineAlertTriangle',
    color: colors.warning[600],
    background: colors.warning[50],
  },
  confirm: {
    name: 'LineThumbsUp',
    color: colors.lavender[600],
    background: colors.lavender[100],
  },
}

export type HeaderIconConfig = {
  name: IconName
  color: string
  background: string
}

type Props = {
  /** Whether the modal is open */
  isOpen: boolean
  /** header icon above title */
  headerIcon?: keyof typeof headerIconConfig
  /** alignment of the icon (default: 'center') */
  headerIconAlignment?: 'left' | 'center'
  /** header title of the modal */
  headerTitle?: string
  /** header subtitle to accompany title */
  headerSubtitle?: React.ReactNode
  /** image to be displayed with header */
  headerImage?: React.ReactNode
  /** aligns both title and subtitle */
  headerTextAlignment?: 'left' | 'center'
  /**
   * How the close button behave
   *
   * - allowed - Clicking the button dismisses the modal (default)
   * - disallowed - The button is hidden.
   * - disabled - The button is visible, but disabled.
   */
  dismissStatus?: 'allowed' | 'disallowed' | 'disabled'
  /** Callback when the user has dismissed the modal */
  onDismiss?: () => void
  /** The children to render */
  children?: ReactNode
  /** Overrides for the modal styling */
  modalStyle?: Partial<CSSProperties>
  /** Overrides for the header styling */
  headerStyle?: Partial<CSSProperties>
  zIndex?: number
  /** Footer section, best suited to action buttons within Fragment */
  footerComponent?: ReactNode
}

export const Modal: React.FC<Props> = ({
  headerTitle,
  headerSubtitle,
  headerImage,
  headerIcon,
  headerIconAlignment = 'center',
  isOpen,
  dismissStatus = 'allowed',
  onDismiss,
  children,
  modalStyle = {},
  headerStyle = {},
  zIndex = 5,
  footerComponent,
  headerTextAlignment = 'center',
}) => {
  const shouldRenderModal = useRenderTimeoutControl({
    shouldRender: isOpen,
    timeoutMs: LocalConstants.AnimationTimingMs as number,
  })

  if (!shouldRenderModal) return null

  const containerClassNames = []
  if (!isOpen) containerClassNames.push('modal-component--unmounting')
  if (headerTextAlignment === 'left') containerClassNames.push('modal-component--text-left')

  const canBeDismissed = dismissStatus === 'allowed'
  const isCloseButtonVisible = dismissStatus !== 'disallowed'
  const shouldShowHeader = headerTitle || isCloseButtonVisible

  return (
    <ModalContainerStyled
      style={{ zIndex }}
      className={containerClassNames.join(' ')}
      role='dialog'
      data-testid='Overlay.Modal'
    >
      <div style={{ ...modalStyle }}>
        {shouldShowHeader && (
          <header style={{ ...headerStyle }}>
            {headerImage && <figure>{headerImage}</figure>}

            {headerIcon && (
              <IconContainerStyled
                headerIconAlignment={headerIconAlignment}
                headerIconBackground={headerIconConfig[headerIcon].background}
              >
                <div>
                  <Icon
                    name={headerIconConfig[headerIcon].name}
                    color={headerIconConfig[headerIcon].color}
                    size='2.4rem'
                  />
                </div>
              </IconContainerStyled>
            )}

            {headerTitle && <h2>{headerTitle}</h2>}

            {headerSubtitle && typeof headerSubtitle === 'string' ? (
              <p>{headerSubtitle}</p>
            ) : (
              headerSubtitle
            )}

            {isCloseButtonVisible && (
              <PillButton
                color='secondary_grey'
                isIconOnlyButton
                onClick={onDismiss}
                aria-label='Close modal'
                disabled={!canBeDismissed}
                size='sm'
              >
                <Icon name='SolidXClose' size='2rem' />
              </PillButton>
            )}
          </header>
        )}

        {children}

        {footerComponent && <footer>{footerComponent}</footer>}
      </div>
    </ModalContainerStyled>
  )
}

// ==============
// #region Styles
// ==============

const ModalContainerStyled = styled.section({
  '--closeButtonSize': '3.6rem',
  '--modalPaddingLeftRightBottom': spacing.md,
  '--modalPaddingTop': spacing.big,

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'fixed',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  margin: 'auto',
  opacity: 0,
  backdropFilter: 'blur(1px)',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  fontFamily: font.family.primary,
  animation: `fadeIn ${LocalConstants.AnimationTimingMs}ms ease forwards`,

  '&.modal-component--unmounting': {
    animation: `fadeOut ${LocalConstants.AnimationTimingMs}ms ease forwards`,
  },

  '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  '@keyframes fadeOut': { from: { opacity: 1 }, to: { opacity: 0 } },

  '> div': {
    boxSizing: 'border-box',
    width: '100%',
    minWidth: `calc(${measurements.mobi.minWidth} - ${spacing.md} * 2)`,
    maxWidth: '60rem',
    margin: `0 ${spacing.md}`,
    padding: 'var(--modalPaddingLeftRightBottom)',
    paddingTop: 'var(--modalPaddingTop)',
    borderRadius: radius.lgx4,
    backgroundColor: colors.white,
    textAlign: 'center',
    overflow: 'hidden',

    '> header': {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: spacing.smx1,
      paddingBottom: spacing.md,
      minHeight: 'var(--closeButtonSize)',

      '> figure': {
        margin: 'calc(-1 * var(--modalPaddingTop)) calc(-1 * var(--modalPaddingLeftRightBottom)) 0',

        '> img': { objectFit: 'cover', width: '100%', maxWidth: '100%', height: 'auto' },
      },

      '> h2': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        margin: 0,
        background: 0,
        color: colors.black,
        fontFamily: font.family.primary,
        fontSize: font.size.xl2.fontSize,
        letterSpacing: font.size.xl2.letterSpacing,
        lineHeight: font.size.xl2.lineHeight,
        fontWeight: font.weight.semibold,
        textShadow: 'none',
        textTransform: 'unset',
        textAlign: 'unset',
        minHeight: 'var(--closeButtonSize)',
        paddingInline: 'var(--closeButtonSize)',
      },

      '> p': {
        margin: 0,
        fontSize: font.size.lg.fontSize,
        letterSpacing: font.size.lg.letterSpacing,
        lineHeight: font.size.lg.lineHeight,
        fontWeight: font.weight.regular,
        color: colors.neutral[800],
        textAlign: 'unset',
        textWrap: 'balance',

        a: { color: 'inherit', textDecoration: 'underline' },
      },

      // Close button
      '> button': { position: 'absolute', top: 0, right: 0 },
    },

    '> footer': {
      display: 'flex',
      gap: spacing.smx1,

      '> button': {
        flex: 1,
        minWidth: 'fit-content',
        whiteSpace: 'nowrap',
      },
    },
  },

  '&.modal-component--text-left': {
    '> div > header': {
      textAlign: 'left',

      '> h2': { justifyContent: 'flex-start', paddingLeft: 0 },
      '> p': { textWrap: 'pretty' },
    },
  },

  '@media (orientation: landscape)': {
    '--modalPaddingTop': spacing.md,

    '> div': {
      maxWidth: '72rem',
      maxHeight: 'calc(100svh - 2rem)',
      overflowY: 'auto',
    },

    '> div > header > figure > img': {
      maxHeight: '44vh',
      height: 'auto',
    },
  },
})

const IconContainerStyled = styled.section(
  ({
    headerIconAlignment,
    headerIconBackground,
  }: {
    headerIconAlignment: 'left' | 'center'
    headerIconBackground: string
  }) => ({
    display: 'flex',
    justifyContent: headerIconAlignment === 'left' ? 'flex-start' : 'center',
    '> div:first-of-type': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '4.8rem',
      height: '4.8rem',
      borderRadius: radius.full,
      background: headerIconBackground,

      '> span': { transform: 'translateY(-0.1rem)' },
    },
  })
)

// #endregion
